<template>
  <v-dialog :model-value="modelValue" @update:model-value="$emit('update:modelValue', $event)" max-width="920" persistent>
    <v-card class="subject-dialog">
      <v-card-title class="d-flex justify-space-between align-center">
        <span>{{ dialogTitle }}</span>
        <div class="d-flex align-center">
          <v-select
            v-model="companyCode"
            :items="companyOptions"
            item-title="title"
            item-value="value"
            label="账套"
            variant="outlined"
            density="compact"
            hide-details
            class="search-input"
            style="width: 160px;margin-right: 16px;"
          />
          <div class="native-date-wrapper" style="position: relative; width: 160px;">
            <v-text-field
              v-model="voucherDate"
              label="凭证日期"
              persistent-placeholder
              variant="outlined"
              density="compact"
              hide-details="auto"
              prepend-inner-icon="mdi-calendar"
              readonly
              class="native-date-field"
              @click="openVoucherDatePicker"
              :title="voucherDate || '点击选择日期'"
            />
            <input
              ref="voucherDateInput"
              type="date"
              v-model="voucherDate"
              style="position: absolute; opacity: 0; pointer-events: none;"
            />
          </div>
          <v-btn icon="mdi-close" variant="text" @click="closeDialog" />
        </div>
      </v-card-title>
      <v-card-text>
        <v-table class="subject-table">
          <thead>
            <tr>
              <th style="width: 40%;">摘要</th>
              <th style="width: 32%;">会计科目</th>
              <th style="width: 12%;">借方金额</th>
              <th style="width: 12%;">贷方金额</th>
              <th style="width: 4%;"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in subjectRows" :key="index">
              <td>
                <v-text-field
                  v-model="row.summary"
                  variant="outlined"
                  density="compact"
                  hide-details
                  class="subject-summary-field"
                />
              </td>
              <td>
                <v-text-field
                  :model-value="row.accountCode ? row.accountCode + '-' + row.accountName : ''"
                  placeholder="请选择会计科目"
                  variant="outlined"
                  density="compact"
                  hide-details
                  readonly
                  class="subject-picker-field"
                  @click="openSubjectPicker(row)"
                />
              </td>
              <td>
                <v-text-field
                  :model-value="formatThousand(row.debit)"
                  @update:model-value="(val) => row.debit = formatAmountInput(val)"
                  variant="outlined"
                  density="compact"
                  hide-details
                  class="text-right subject-amount-field"
                />
              </td>
              <td>
                <v-text-field
                  :model-value="formatThousand(row.credit)"
                  @update:model-value="(val) => row.credit = formatAmountInput(val)"
                  variant="outlined"
                  density="compact"
                  hide-details
                  class="text-right subject-amount-field"
                />
              </td>
              <!-- v-if="row.userAdd" -->
              <td class="text-center">
                <v-btn
                  icon="mdi-delete"
                  variant="text"
                  color="error"
                  density="compact"
                  size="small"
                  class="row-del-btn"
                  @click="removeRow(index)"
                />
              </td>
            </tr>
          </tbody>
        </v-table>
        <v-btn variant="text" class="add-row-btn mt-4" block @click="addTemplateRow">
          <v-icon icon="mdi-plus" class="mr-1" />
          增加一行
        </v-btn>
      </v-card-text>
      <v-card-actions class="justify-end">
        <v-btn color="primary" variant="flat" @click="handlePrint">打印凭证</v-btn>
        <v-btn color="error" variant="tonal" style="background-color: #453237!important;" @click="closeDialog">取消</v-btn>
        <v-btn color="primary" variant="flat" @click="handleSave">保存</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- 科目选择弹窗 -->
  <v-dialog v-model="showSubjectPicker" max-width="480" persistent>
    <v-card class="subject-picker-dialog">
      <v-card-title class="d-flex justify-space-between align-center">
        <span>选择科目</span>
        <v-btn icon="mdi-close" variant="text" @click="showSubjectPicker = false" />
      </v-card-title>
      <v-card-text>
        <v-text-field
          v-model="pickerSearch"
          placeholder="编号/名称"
          label="搜索科目"
          persistent-placeholder
          prepend-inner-icon="mdi-magnify"
          variant="outlined"
          density="compact"
          hide-details
          class="mb-4"
          style="color: #fff;"
          clearable
        />
        <div class="picker-list">
          <v-radio-group v-model="pickerSelectedCode" class="picker-radio-group">
            <v-radio
              v-for="opt in filteredSubjectOptions"
              :key="opt.code"
              :label="opt.label"
              :value="opt.code"
              density="compact"
              class="picker-radio"
            />
          </v-radio-group>
          <div v-if="filteredSubjectOptions.length === 0" class="text-grey text-center py-4">
            未找到匹配的科目
          </div>
        </div>
      </v-card-text>
      <v-card-actions class="justify-end">
        <v-btn color="error" variant="tonal" style="background-color: #453237!important;" @click="showSubjectPicker = false">取消</v-btn>
        <v-btn color="primary" variant="flat" @click="confirmSubjectPick">确认</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts" setup>
import { ref, computed, watch, nextTick } from 'vue'
import { purchaseApi } from '@/services/api'
import { useSnackbar } from '@/composables/useSnackbar'
import { printVoucherAllReceipt } from '@/utils/printVoucher'
import logoUrl from '@/assets/jf.png'

const { showSuccess, showError } = useSnackbar()

const props = defineProps<{
  modelValue: boolean
  reconciliationType: string
  item: any
  /** 自定义加载详情方法，不传则使用默认 purchaseApi.getPrintDetail */
  loadDetail?: (params: { id: any; reconciliationType: string }) => Promise<any>
  /** 自定义保存方法，不传则使用默认 purchaseApi.savePrintDetail */
  saveDetail?: (params: { id: any; printDetail: any }) => Promise<any>
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', val: boolean): void
  (e: 'save'): void
  (e: 'error', msg: string): void
}>()

// 对话框标题
const dialogTitle = computed(() => {
  const type = props.reconciliationType
  if (type === 'prepayment') return '预付款凭证'
  if (type === 'prepaymentCancel') return '预付销账凭证'
  if (type === 'payableCancel') return '应付销账凭证'
  if (type === 'expenseClaim') return '费用报销凭证'
  if (type === 'material') return '原材料入库凭证'
  return '付款凭证'
})

// 账套选项
const companyOptions = [
  { value: 1, title: '上海江丰' },
  { value: 2, title: '优罗莎' },
  { value: 3, title: '高登' }
]

const companyCode = ref<any>(null)
const voucherDate = ref('')
const voucherDateInput = ref<HTMLInputElement>()
const subjectRows = ref<any[]>([])
const subjectOptions = ref<any[]>([])

// 科目选择弹窗
const showSubjectPicker = ref(false)
const pickerSearch = ref('')
const pickerSelectedCode = ref('')
const currentPickerRow = ref<any>(null)

const filteredSubjectOptions = computed(() => {
  const keyword = pickerSearch.value.trim().toLowerCase()
  if (!keyword) return subjectOptions.value
  return subjectOptions.value.filter((s: any) =>
    (s.label || '').toLowerCase().includes(keyword) ||
    (s.code || '').toLowerCase().includes(keyword) ||
    (s.name || '').toLowerCase().includes(keyword)
  )
})

const getCompanyCodeTitle = (code: number | null) => {
  const company = companyOptions.find(c => c.value === code)
  return company ? company.title : ''
}

const getCompanyTitleCode = (title: string) => {
  const company = companyOptions.find(c => c.title === title)
  return company ? company.value : null
}

// 格式化日期
const formatDate = (timestamp: number) => {
  if (!timestamp) return '/'
  const date = new Date(timestamp)
  return date.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).replace(/\//g, '/')
}

// 千分位格式化
const formatThousand = (val: any) => {
  if (val === null || val === undefined || val === '') return ''
  const str = String(val)
  const parts = str.split('.')
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return parts.join('.')
}

// 格式化金额输入
const formatAmountInput = (val: any) => {
  if (val === null || val === undefined || val === '') return ''
  let str = String(val)
  str = str.replace(/,/g, '')
  str = str.replace(/[^\d.]/g, '')
  const parts = str.split('.')
  if (parts.length > 2) {
    str = parts[0] + '.' + parts.slice(1).join('')
  }
  if (parts[1] && parts[1].length > 4) {
    str = parts[0] + '.' + parts[1].slice(0, 4)
  }
  if (str.length > 1 && str.startsWith('0') && str[1] !== '.') {
    str = str.replace(/^0+/, '')
  }
  return str
}

const openVoucherDatePicker = () => {
  const input = voucherDateInput.value
  if (input) {
    try {
      if (input.showPicker) {
        input.showPicker()
      } else {
        input.click()
      }
    } catch (error) {
      console.error('打开日期选择器失败:', error)
    }
  }
}

const addTemplateRow = () => {
  subjectRows.value.push({
    summary: '',
    accountCode: '',
    accountName: '',
    debit: '',
    credit: '',
    type: 'expense',
    userAdd: true
  })
}

const removeRow = (index: number) => {
  subjectRows.value.splice(index, 1)
}

const openSubjectPicker = (row: any) => {
  currentPickerRow.value = row
  pickerSelectedCode.value = row.accountCode || ''
  pickerSearch.value = ''
  showSubjectPicker.value = true
}

const confirmSubjectPick = () => {
  if (!currentPickerRow.value) return
  const subject = subjectOptions.value.find((s: any) => s.code === pickerSelectedCode.value)
  if (subject) {
    currentPickerRow.value.accountName = subject.name
    currentPickerRow.value.accountCode = subject.code
  }
  showSubjectPicker.value = false
  currentPickerRow.value = null
  pickerSelectedCode.value = ''
  pickerSearch.value = ''
}

const loadVoucherList = async () => {
  let debitCode = ''
  let creditorCode = ''
  const type = props.reconciliationType
  if (type === 'prepayment') {
    debitCode = '1123'
    creditorCode = '1002'
  } else if (type === 'prepaymentCancel') {
    debitCode = '2202'
    creditorCode = '1123'
  } else if (type === 'payableCancel') {
    debitCode = '2204'
    creditorCode = '2202'
  } else if (type === 'payment') {
    debitCode = '2202'
    creditorCode = '1002'
  } else if (type === 'material') {
    debitCode = '1403'
    creditorCode = '2204'
  }
  try {
    const res = await purchaseApi.getVoucherList({
      keyword: pickerSearch.value,
      debitCode,
      creditorCode
    })
    if (res?.code === 0) {
      subjectOptions.value = (res.data || []).map((item: any) => ({
        ...item,
        label: item.code + '-' + item.name
      }))
    } else {
      showError(res?.message || '获取科目列表失败')
    }
  } catch (error: any) {
    showError(error.message || '获取科目列表失败')
  }
}

const loadPrintDetail = async () => {
  const item = props.item
  if (!item) return

  try {
    const loadFn = props.loadDetail || purchaseApi.getPrintDetail
    const detailRes = await loadFn({
      id: item.id,
      reconciliationType: props.reconciliationType
    })
    if (detailRes?.code === 0 && detailRes.data?.rows) {
      const savedRows = detailRes.data.rows.map((row: any) => ({
        ...row,
        userAdd: row.userAdd ?? false
      }))
      subjectRows.value = savedRows
      voucherDate.value = (detailRes.data.date || formatDate(item.createTime)).replace(/\//g, '-')
      const savedCompany = detailRes.data?.companyCode
      if (savedCompany) {
        const matched = companyOptions.find(opt => opt.value === savedCompany)
        if (matched) {
          companyCode.value = matched
        }
      } else {
        companyCode.value = getCompanyTitleCode(detailRes.data?.company) || ''
      }
    } else {
      showError(detailRes?.message || '获取打印数据失败')
      subjectRows.value = []
    }
  } catch {
    subjectRows.value = []
  }

  // 如果 companyCode 没有值，根据付款公司匹配账套
  if (!companyCode.value && item.paymentCompany) {
    const matched = companyOptions.find(opt => opt.title === item.paymentCompany)
    if (matched) {
      companyCode.value = matched.value
    }
  }
}

const handleSave = async () => {
  const item = props.item
  const printDetail = {
    company: item?.paymentCompany,
    companyCode: companyCode.value?.value || companyCode.value,
    date: voucherDate.value.replace(/-/g, '/'),
    voucherNo: item?.voucherNo || '/',
    currency: '人民币',
    unit: '元',
    operator: item?.uploadUserName,
    reconciliationType: props.reconciliationType,
    rows: subjectRows.value.map((row: any) => ({
      summary: row.summary,
      accountCode: row.accountCode,
      accountName: row.accountName,
      debit: Number(row.debit) || 0,
      credit: Number(row.credit) || 0,
      type: row.type,
      userAdd: row.userAdd || false
    }))
  }

  try {
    const saveFn = props.saveDetail || purchaseApi.savePrintDetail
    const res = await saveFn({
      id: item?.id,
      printDetail
    })
    if (res?.code === 0) {
      showSuccess('设置科目成功')
      emit('save')
      closeDialog()
    } else {
      showError(res?.message || '保存失败')
      emit('error', res?.message || '保存失败')
    }
  } catch (error: any) {
    showError(error.message || '保存失败')
    emit('error', error.message || '保存失败')
  }
}

/** 将图片 URL 转为 base64 DataURL */
async function getImageBase64(url: string) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.width
      canvas.height = img.height
      const ctx = canvas.getContext('2d')!
      ctx.drawImage(img, 0, 0)
      resolve(canvas.toDataURL('image/png'))
    }
    img.onerror = reject
    img.src = url
  })
}

const doPrint = async () => {
  const item = props.item
  let printDetail: any = null
  try {
    const loadFn = props.loadDetail || purchaseApi.getPrintDetail
    const res = await loadFn({
      id: item.id,
      reconciliationType: props.reconciliationType
    })
    if (res?.code === 0 && res.data) {
      printDetail = res.data
    } else {
      showError(res?.message || '获取打印数据失败')
      return
    }
  } catch {
    showError('获取打印数据失败')
    return
  }

  // const rows = (printDetail?.rows || []).filter(
  //   (r: any) => Number(r.debit) !== 0 || Number(r.credit) !== 0
  // )
  
  const rows = printDetail?.rows || []
  if (rows.length === 0) {
    showError('没有可打印的凭证数据')
    return
  }
  try {
    let logoBase64 = ''
    try {
      logoBase64 = await getImageBase64(logoUrl)
    } catch {
      // 图片加载失败时继续使用文字 logo
    }

    printVoucherAllReceipt({
      company: printDetail?.company || item.paymentCompany,
      companyCode: getCompanyCodeTitle(printDetail.companyCode) || '',
      date: printDetail?.date || formatDate(item.createTime),
      voucherNo: printDetail?.voucherNo || item.voucherNo || '/',
      expenseAccount: '',
      taxAccount: '',
      payableAccount: '',
      operator: printDetail?.operator || item.uploadUserName,
      applications: [],
      rows,
      logoBase64
    })
  } catch (e: any) {
    showError(e.message || '打印失败')
  }
}

const handlePrint = async () => {
  await handleSaveWithoutClose()
  if (props.item) {
    await doPrint()
  }
}

const handleSaveWithoutClose = async () => {
  const item = props.item
  const printDetail = {
    company: item?.paymentCompany,
    companyCode: companyCode.value?.value || companyCode.value,
    date: voucherDate.value.replace(/-/g, '/'),
    voucherNo: item?.voucherNo || '/',
    currency: '人民币',
    unit: '元',
    operator: item?.uploadUserName,
    reconciliationType: props.reconciliationType,
    rows: subjectRows.value.map((row: any) => ({
      summary: row.summary,
      accountCode: row.accountCode,
      accountName: row.accountName,
      debit: Number(row.debit) || 0,
      credit: Number(row.credit) || 0,
      type: row.type,
      userAdd: row.userAdd || false
    }))
  }

  try {
    const saveFn = props.saveDetail || purchaseApi.savePrintDetail
    const res = await saveFn({
      id: item?.id,
      printDetail
    })
    if (res?.code === 0) {
      // 不关闭弹窗，不显示成功提示
    } else {
      showError(res?.message || '保存失败')
    }
  } catch (error: any) {
    showError(error.message || '保存失败')
  }
}

const closeDialog = () => {
  emit('update:modelValue', false)
}

// 监听显示状态，打开时加载数据
watch(() => props.modelValue, (val) => {
  if (val && props.item) {
    nextTick(() => {
      loadVoucherList()
      loadPrintDetail()
    })
  } else if (!val) {
    // 关闭时重置数据
    subjectRows.value = []
    voucherDate.value = ''
    companyCode.value = null
    subjectOptions.value = []
  }
})
</script>

<style lang="scss" scoped>
.subject-dialog {
  background-color: #2B2D33 !important;
}

.subject-dialog :deep(.v-card-title) {
  color: #fff;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  padding: 16px 24px;
  font-size: 18px;
  font-weight: 500;
}

.subject-dialog :deep(.v-card-text) {
  padding: 24px;
}

.subject-dialog :deep(.v-card-actions) {
  padding: 16px 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
}

.subject-dialog :deep(.v-select .v-field__input),
.subject-dialog :deep(.v-text-field .v-field__input) {
  color: #fff !important;
  padding-inline: 4px !important;
}

.subject-dialog :deep(.v-select .v-field__outline),
.subject-dialog :deep(.v-text-field .v-field__outline) {
  color: rgba(255, 255, 255, 0.12) !important;
}

.subject-dialog :deep(.v-field--variant-outlined .v-field__outline__start) {
  flex: 0;
}

.subject-dialog :deep(.v-label) {
  color: rgba(255, 255, 255, 0.7) !important;
}

.subject-dialog :deep(.v-select .v-field__append-inner i) {
  color: rgba(255, 255, 255, 0.7) !important;
}

.subject-amount-field :deep(.v-field__input) {
  text-align: right !important;
}

.subject-amount-field :deep(input::-webkit-outer-spin-button),
.subject-amount-field :deep(input::-webkit-inner-spin-button) {
  -webkit-appearance: none;
  margin: 0;
}

.subject-amount-field :deep(input[type="number"]) {
  -moz-appearance: textfield;
  appearance: textfield;
}

.subject-dialog :deep(.v-list) {
  background-color: #2B2D33 !important;
}

.subject-dialog :deep(.v-list-item) {
  color: #fff !important;
}

.subject-dialog :deep(.v-list-item:hover) {
  background-color: rgba(255, 255, 255, 0.08) !important;
}

.subject-dialog :deep(.v-list-item-title) {
  color: #fff !important;
}

:deep(.subject-dialog th),
:deep(.subject-dialog td) {
  padding: 0px 4px !important;
}

.subject-table {
  background-color: #2B2D33 !important;
  color: #fff !important;
}

.subject-table :deep(thead) {
  background-color: #2B2D33 !important;
}

.subject-table :deep(th) {
  color: rgba(255, 255, 255, 0.7) !important;
  background-color: #2B2D33 !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12) !important;
}

.subject-table :deep(tbody) {
  background-color: #2B2D33 !important;
}

.subject-table :deep(td) {
  color: #fff !important;
  background-color: #2B2D33 !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
}

.subject-table :deep(tr:hover td) {
  background-color: rgba(255, 255, 255, 0.04) !important;
}

.subject-picker-field :deep(.v-field__input) {
  cursor: pointer;
}

.subject-picker-dialog {
  background-color: #2B2D33 !important;
}

.subject-picker-dialog :deep(.v-card-title) {
  color: #fff;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  padding: 16px 24px;
  font-size: 18px;
  font-weight: 500;
}

.subject-picker-dialog :deep(.v-card-text) {
  padding: 16px 24px;
}

.subject-picker-dialog :deep(.v-card-actions) {
  padding: 16px 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
}

.picker-list {
  max-height: 320px;
  overflow-y: auto;
}

:deep(.picker-list)::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

:deep(.picker-list)::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
}

:deep(.picker-list)::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  transition: all 0.3s ease;
}

:deep(.picker-list)::-webkit-scrollbar-thumb:hover {
  background-color: rgba(255, 255, 255, 0.3);
}

.picker-radio-group :deep(.v-selection-control) {
  min-height: 36px;
}

.picker-radio :deep(.v-label) {
  color: #fff !important;
  font-size: 14px;
}

.picker-radio :deep(.v-selection-control__input) {
  color: #D2475D;
}

.add-row-btn {
  text-transform: none;
  letter-spacing: 0;
  border: 1px dashed rgba(255, 255, 255, 0.2) !important;
  color: rgba(255, 255, 255, 0.6) !important;

  &:hover {
    border-color: #D2475D !important;
    color: #D2475D !important;
  }
}

.row-del-btn {
  color: rgba(255, 255, 255, 0.5) !important;

  &:hover {
    color: #D2475D !important;
    background: rgba(210, 71, 93, 0.1) !important;
  }
}
</style>
